import { useEffect, useRef } from 'react'
import { useDispatch } from 'react-redux'
import type { AppDispatch } from '../../../shared/store'
import {
  checkSession,
  setPasswordRecovery,
  setSession,
} from '../../../shared/store/slices/authSlice'
import { queryClient } from '../../../shared/lib/queryClient'
import { authService } from '../services/authService'

interface AuthProviderProps {
  children: React.ReactNode
}

/**
 * AuthProvider component
 * - Checks for existing session on mount
 * - Subscribes to auth state changes
 * - Keeps Redux store in sync with Supabase auth
 */
export const AuthProvider = ({ children }: AuthProviderProps) => {
  const dispatch = useDispatch<AppDispatch>()
  // undefined = no auth event seen yet this page load (distinct from null = signed out)
  const lastUserIdRef = useRef<string | null | undefined>(undefined)

  useEffect(() => {
    // Check for existing session on mount
    void dispatch(checkSession())

    // Subscribe to auth state changes
    const subscription = authService.onAuthStateChange((session, event) => {
      // Raised when detectSessionInUrl consumes a recovery token. The routes
      // hold the user on /reset-password until they set a password; without
      // this the session looks like an ordinary sign-in and the link appears
      // to have done nothing.
      if (event === 'PASSWORD_RECOVERY') dispatch(setPasswordRecovery(true))
      if (event === 'SIGNED_OUT') dispatch(setPasswordRecovery(false))

      // Server data cached by TanStack Query is not user-scoped; drop it whenever
      // the signed-in user changes (sign-out or account switch) so the next user
      // never sees the previous user's jobs. Token refreshes keep the same id.
      const userId = session?.user.id ?? null
      if (lastUserIdRef.current !== undefined && lastUserIdRef.current !== userId) {
        queryClient.clear()
      }
      lastUserIdRef.current = userId

      if (session) {
        // Fetch role from database asynchronously
        void (async () => {
          const { role } = await authService.getUserRole(session.user.id)
          dispatch(
            setSession({
              user: session.user,
              session,
              role,
            })
          )
        })()
      } else {
        dispatch(
          setSession({
            user: null,
            session: null,
            role: null,
          })
        )
      }
    })

    // Cleanup subscription on unmount
    return () => {
      subscription.unsubscribe()
    }
  }, [dispatch])

  return <>{children}</>
}
